import { useState } from "react";
import api from "../../api";
import AgolCheck from "../ui/AgolCheck";
import SubgridLayerSettings from "./SubgridLayerSettings";
import { getSubgridLayerId } from "../../lib/subgridLayer";
import { getTreatmentLayerId } from "../../lib/treatmentLayer";
import { mdyToIso } from "../../lib/treatmentArea";
import {
  Field,
  DateInput,
  Actions,
  PrimaryButton,
  GhostButton,
  ResultBanner,
  CardIntro,
} from "../ui/Form";

export default function MarkTreatedPanel() {
  const [labels, setLabels] = useState("");
  const [dateTreated, setDateTreated] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [filling, setFilling] = useState(false);
  const [result, setResult] = useState<any>(null);

  /** Pull the subgrid list straight off this week's scheduled treatment areas instead of
   *  retyping it. Uses each area's GridLabels, which is geometry-derived, so an area that
   *  straddles two subgrids contributes both. */
  const fillFromScheduled = async () => {
    setFilling(true);
    setResult(null);
    try {
      const res = await api.post("/list-treatment-areas", {
        layer_id: getTreatmentLayerId(),
        area_status: "Upcoming",
      });
      const areas: any[] = res.data?.areas || [];
      const found = Array.from(new Set(areas.flatMap((a) => a.gridlabels || []))).sort();
      if (!found.length) {
        setResult({
          status: "failure",
          message: areas.length
            ? "Scheduled areas found, but none carry grid labels."
            : "No scheduled areas on the treatment layer — schedule them in the Treatment Builder first.",
        });
        return;
      }
      setLabels(found.join(", "));
      // Adopt the scheduled treatment date when every area agrees on one
      const dates = Array.from(new Set(areas.map((a) => a.treatment_date).filter(Boolean)));
      if (dates.length === 1) {
        const iso = mdyToIso(dates[0] as string);
        if (iso) setDateTreated(iso);
      }
      setResult({
        status: "success",
        message: `Filled ${found.length} subgrid${found.length === 1 ? "" : "s"} from ${areas.length} scheduled area${areas.length === 1 ? "" : "s"}.`,
      });
    } catch (err: any) {
      setResult({ status: "failure", message: err?.response?.data?.message || err.message });
    } finally {
      setFilling(false);
    }
  };

  const run = async () => {
    const gridlabels = labels.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!gridlabels.length) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/update-subgrid-status", {
        gridlabels,
        layer_id: getSubgridLayerId() || undefined,
        date_treated: dateTreated || undefined,
      });
      const updated: string[] = res.data.updated || [];
      const missing: string[] = res.data.missing || [];
      setResult({
        // Nothing updated is a failure in practice, even though the request
        // succeeded -- the operator needs to notice.
        status: updated.length ? "success" : "failure",
        message: updated.length
          ? `Marked ${updated.length} subgrid${updated.length === 1 ? "" : "s"} as Treated.`
          : "No matching subgrids were found, so nothing was updated.",
        details: [
          updated.length ? `updated: ${updated.join(", ")}` : "",
          missing.length ? `not found: ${missing.join(", ")}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
      });
    } catch (err: any) {
      setResult({ status: "failure", message: err?.response?.data?.message || err.message });
    } finally {
      setBusy(false);
    }
  };

  const count = labels.split(/[,\s]+/).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <AgolCheck layerLabel="Treatment Subgrid" testEndpoint="/test-spray-layers" />
      <SubgridLayerSettings />

      <CardIntro>
        Marks subgrids as <b>Treated</b> on the surveillance dashboard. Enter the grid
        labels from your run — works from any device.
      </CardIntro>

      <Field label="Date treated">
        <DateInput
          value={dateTreated}
          onChange={(e) => setDateTreated(e.target.value)}
          disabled={busy}
          className="max-w-[12rem]"
        />
      </Field>

      <GhostButton onClick={fillFromScheduled} busy={filling} busyLabel="Loading…" disabled={busy}>
        ⤓ Fill from this week&rsquo;s scheduled areas
      </GhostButton>

      <Field
        label="Grid labels"
        hint={count ? `${count} subgrid${count === 1 ? "" : "s"} entered` : "Comma or space separated"}
      >
        <textarea
          value={labels}
          onChange={(e) => setLabels(e.target.value)}
          placeholder="e.g. 122126-1, 132103-1, 142116-3"
          rows={3}
          disabled={busy}
          className="w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-brand-tint disabled:opacity-50"
        />
      </Field>

      <Actions>
        <PrimaryButton onClick={run} busy={busy} busyLabel="Updating…" disabled={!labels.trim()}>
          Mark Treated on AGOL
        </PrimaryButton>
        {labels && (
          <GhostButton
            onClick={() => {
              setLabels("");
              setResult(null);
            }}
            disabled={busy}
          >
            Clear
          </GhostButton>
        )}
      </Actions>

      <ResultBanner result={result} />
    </div>
  );
}
